import { restoreSession, isAuthenticated, onAuthChange, getCurrentUser, isSiteAdmin } from "./auth.js";

import * as loginView from "./views/login.js";
import * as registerView from "./views/register.js";
import * as feedView from "./views/feed.js";
import * as postDetailView from "./views/postDetail.js";
import * as createPostView from "./views/createPost.js";
import * as divePlansView from "./views/divePlans.js";
import * as divePlanDetailView from "./views/divePlanDetail.js";
import * as createDivePlanView from "./views/createDivePlan.js";
import * as diveSitesView from "./views/diveSites.js";
import * as diveSiteDetailView from "./views/diveSiteDetail.js";
import * as createDiveSiteView from "./views/createDiveSite.js";
import * as importDiveSitesView from "./views/importDiveSites.js";
import * as communitiesView from "./views/communities.js";
import * as communityDetailView from "./views/communityDetail.js";
import * as createCommunityView from "./views/createCommunity.js";
import * as communityMembersView from "./views/communityMembers.js";
import * as userProfileView from "./views/userProfile.js";
import * as photoDetailView from "./views/photoDetail.js";
import * as searchView from "./views/search.js";
import * as notificationsView from "./views/notifications.js";
import * as editProfileView from "./views/editProfile.js";
import * as verifyEmailView from "./views/verifyEmail.js";
import * as cohortInviteView from "./views/cohortInvite.js";
import * as forgotPasswordView from "./views/forgotPassword.js";
import * as resetPasswordView from "./views/resetPassword.js";
import * as moderationView from "./views/moderation.js";
import { initVerificationNotices, renderVerificationBanner } from "./verificationNotice.js";
import { initSearchControl, refreshSearchVisibility } from "./search.js";
import { initBell, refreshBellVisibility } from "./notifications.js";
import { initChatBadge, refreshChatBadgeVisibility, paintChatBadge } from "./chatBadge.js";
import { rememberReturn } from "./returnTo.js";

// Each route: a list of static/':param' segments, the view module, and
// whether the user must be signed in to see it.
const ROUTES = [
  { segments: ["login"], view: loginView, protected: false },
  { segments: ["register"], view: registerView, protected: false },
  // Unprotected: the signed token in the URL is the credential, and mail
  // clients open links wherever, often with no session.
  { segments: ["verify", ":token"], view: verifyEmailView, protected: false },
  // Also unprotected: someone resetting a password is by definition not
  // signed in, and the signed token is the credential.
  { segments: ["forgot-password"], view: forgotPasswordView, protected: false },
  { segments: ["reset", ":token"], view: resetPasswordView, protected: false },
  // Also open: an emailed cohort invitation shows the cohort before signing in.
  { segments: ["cohort-invite", ":token"], view: cohortInviteView, protected: false },
  { segments: ["feed"], view: feedView, protected: true, navKey: "feed" },
  { segments: ["dive-plans"], view: divePlansView, protected: true, navKey: "dive-plans" },
  { segments: ["dive-plan", ":id"], view: divePlanDetailView, protected: true },
  { segments: ["dive-plan", ":id", "edit"], view: createDivePlanView, protected: true },
  { segments: ["create-dive-plan"], view: createDivePlanView, protected: true },
  // Open to visitors: dive sites are public (the API serves them to anyone),
  // and each page offers to log in or sign up (signInPrompt.js).
  { segments: ["dive-sites"], view: diveSitesView, protected: false, navKey: "dive-sites" },
  { segments: ["dive-site", ":id"], view: diveSiteDetailView, protected: false },
  { segments: ["dive-site", ":id", "edit"], view: createDiveSiteView, protected: true },
  { segments: ["create-dive-site"], view: createDiveSiteView, protected: true },
  { segments: ["import-dive-sites"], view: importDiveSitesView, protected: true },
  { segments: ["cohorts"], view: communitiesView, protected: true, navKey: "cohorts" },
  { segments: ["cohort", ":id"], view: communityDetailView, protected: true },
  { segments: ["cohort", ":id", "edit"], view: createCommunityView, protected: true },
  { segments: ["cohort", ":id", "members"], view: communityMembersView, protected: true },
  // A cohort's chat is the Cohorts page with that cohort chosen.
  { segments: ["cohort", ":id", "chat"], view: communitiesView, protected: true },
  { segments: ["create-cohort"], view: createCommunityView, protected: true },
  // Old links and bookmarks still resolve. Hash routes cost nothing to
  // keep, and a shared #/community/5 URL outliving the rename is exactly
  // the kind of breakage nobody reports.
  { segments: ["communities"], view: communitiesView, protected: true, navKey: "cohorts" },
  { segments: ["community", ":id"], view: communityDetailView, protected: true },
  { segments: ["community", ":id", "edit"], view: createCommunityView, protected: true },
  { segments: ["community", ":id", "members"], view: communityMembersView, protected: true },
  { segments: ["community", ":id", "chat"], view: communitiesView, protected: true },
  { segments: ["create-community"], view: createCommunityView, protected: true },
  { segments: ["post", ":id"], view: postDetailView, protected: true },
  { segments: ["post", ":id", "edit"], view: createPostView, protected: true },
  { segments: ["create-post"], view: createPostView, protected: true },
  { segments: ["moderation"], view: moderationView, protected: true, navKey: "moderation" },
  { segments: ["profile"], view: userProfileView, protected: true, navKey: "profile" },
  { segments: ["profile", "edit"], view: editProfileView, protected: true },
  { segments: ["user", ":id"], view: userProfileView, protected: true },
  { segments: ["photo", ":id"], view: photoDetailView, protected: true },
  { segments: ["search"], view: searchView, protected: true },
  { segments: ["notifications"], view: notificationsView, protected: true },
];

export function navigate(path) {
  location.hash = `#${path}`;
}

function parseHash() {
  const raw = location.hash.replace(/^#/, "") || "/feed";
  const [pathPart, queryPart] = raw.split("?");
  const segments = pathPart.split("/").filter(Boolean);
  const query = new URLSearchParams(queryPart || "");
  return { segments, query, path: pathPart };
}

function matchRoute(segments) {
  for (const route of ROUTES) {
    if (route.segments.length !== segments.length) continue;
    const params = {};
    const isMatch = route.segments.every((seg, i) => {
      if (seg.startsWith(":")) {
        params[seg.slice(1)] = segments[i];
        return true;
      }
      return seg === segments[i];
    });
    if (isMatch) return { route, params };
  }
  return null;
}

function renderNav() {
  const navLinks = document.getElementById("nav-links");
  const navAuth = document.getElementById("nav-auth-actions");
  const { segments } = parseHash();
  const activeKey = (segments[0] === "cohort" || segments[0] === "community" ||
                     segments[0] === "communities")
    ? "cohorts"
    : segments[0] || "feed";

  if (isAuthenticated()) {
    // The app's bottom-bar labels (flutter-app/lib/screens/home_screen.dart),
    // short as they have to be there; the pages keep their full titles
    // (Following, Dive Events, Dive Sites). Following stays at #/feed, so
    // bookmarks and the landing page still work.
    const items = [
      { key: "cohorts", label: "Cohorts", icon: "bi-people", path: "/cohorts" },
      { key: "feed", label: "Feed", icon: "bi-chat-square-text", path: "/feed" },
      { key: "dive-plans", label: "Events", icon: "bi-calendar-event", path: "/dive-plans" },
      { key: "dive-sites", label: "Sites", icon: "bi-compass", path: "/dive-sites" },
    ];
    // Only moderators see the queue; the API enforces it regardless.
    if (isSiteAdmin()) {
      items.push({ key: "moderation", label: "Moderation", icon: "bi-flag", path: "/moderation" });
    }
    navLinks.innerHTML = items
      .map(
        (item) => `
      <li class="nav-item">
        <a class="nav-link ${activeKey === item.key ? "active" : ""}" href="#${item.path}">
          <i class="bi ${item.icon} me-1"></i>${item.label}${item.key === "cohorts"
            ? `<span class="badge rounded-pill text-bg-danger ms-1 d-none" id="nav-cohorts-unread"></span>`
            : ""}
        </a>
      </li>`
      )
      .join("");

    const me = getCurrentUser();
    const name = me ? (me.display_name || me.username) : "Profile";
    navAuth.innerHTML = `
      <li class="nav-item">
        <a class="nav-link ${activeKey === "profile" || activeKey === "user" ? "active" : ""}" href="#/profile">
          <i class="bi bi-person-circle me-1"></i>${name}
        </a>
      </li>`;
  } else {
    // What a visitor can see without an account.
    navLinks.innerHTML = `
      <li class="nav-item">
        <a class="nav-link ${activeKey === "dive-sites" || activeKey === "dive-site" ? "active" : ""}" href="#/dive-sites">
          <i class="bi bi-compass me-1"></i>Sites
        </a>
      </li>`;
    navAuth.innerHTML = `
      <li class="nav-item"><a class="nav-link" href="#/login">Log in</a></li>
      <li class="nav-item"><a class="nav-link" href="#/register">Sign up</a></li>`;
  }

  // The navbar was just rebuilt: put the Cohorts badge's count back on it.
  paintChatBadge();
  closeMobileNavOnLinkClick();
}

/** On mobile, the nav collapses into a dropdown panel (see navbar-toggler
 * in index.html). Without this, tapping a link navigates correctly but
 * leaves the dropdown panel open on top of the new page — every nav link
 * needs to close it first. Safe to call at desktop widths too: Bootstrap
 * forces the collapse to plain flex display via CSS there regardless of
 * the JS-controlled "show" class, so hide() is a harmless no-op. */
function closeMobileNavOnLinkClick() {
  const collapseEl = document.getElementById("navbarContent");
  if (!collapseEl || typeof bootstrap === "undefined") return;
  collapseEl.querySelectorAll(".nav-link").forEach((link) => {
    link.addEventListener("click", () => {
      const instance = bootstrap.Collapse.getOrCreateInstance(collapseEl, { toggle: false });
      instance.hide();
    });
  });
}

/** Renders a route into #app.
 *
 * A view must bind its event handlers in the same synchronous run as it
 * writes its markup — never `innerHTML = ...`, then `await`, then
 * addEventListener. Two renders can land on #app close together, and the
 * second one's innerHTML destroys the first one's markup: a view that
 * binds after awaiting then binds to whatever form survived, and one
 * click submits twice. That created duplicate dive events until
 * createDivePlan and createPost were changed to bind first and resolve
 * their picker labels afterwards. */
async function renderRoute() {
  const appEl = document.getElementById("app");
  const { segments, query } = parseHash();
  const matched = matchRoute(segments);

  renderNav();
  renderVerificationBanner();

  if (!matched) {
    appEl.innerHTML = `<div class="dc-empty"><i class="bi bi-question-circle"></i><div class="fw-semibold">Page not found</div></div>`;
    return;
  }

  const { route, params } = matched;

  if (route.protected && !isAuthenticated()) {
    // Back here once signed in: a visitor on a public dive site page who
    // opens a post or event lands on it, not the feed.
    rememberReturn(location.hash.replace(/^#/, "") || "/feed");
    navigate("/login");
    return;
  }
  const isAuthPage = route.segments[0] === "login" || route.segments[0] === "register";
  if (isAuthPage && isAuthenticated()) {
    // Already signed in and hitting login/register — send them to the feed.
    navigate("/feed");
    return;
  }

  await route.view.render(appEl, params, query);
}

window.addEventListener("hashchange", renderRoute);

/** What the router cares about: who you are, and whether you are allowed
 *  in. Deliberately not the whole user — a display name or description
 *  change must not read as an access change. */
function accessKey() {
  const user = getCurrentUser();
  return user ? `${user.id}:${user.email_verified === true}` : "anonymous";
}

let lastAccessKey = accessKey();

onAuthChange(() => {
  renderNav();
  renderVerificationBanner();
  refreshSearchVisibility();
  refreshBellVisibility();
  refreshChatBadgeVisibility();
  // Only re-evaluate the route when access could actually have changed —
  // signing in or out, becoming a different user, or confirming an email.
  //
  // Saving a profile field also notifies, and re-rendering there rebuilt
  // the Edit Profile page from scratch, destroying the form the user was
  // still looking at along with the "saved" confirmation it had just
  // set — so a successful save looked like nothing had happened.
  const key = accessKey();
  if (key !== lastAccessKey) {
    lastAccessKey = key;
    // During boot this notification is just the session being restored,
    // and boot() renders straight afterwards. Rendering here too is a
    // wasted round trip on every page load.
    if (!booting) renderRoute();
  }
});

/** Where someone arriving at the bare address goes: their feed, if signed in.
 *
 * A visitor on the production app goes to the landing page, whose "Open the
 * app" brings them back to the dive sites. Only there: the same build serves
 * test.divecohorts.com and local development, where the landing page's
 * buttons would lead off to production, so a visitor there lands on the dive
 * sites directly. Returns { url } to leave for, or { hash } to route to. */
export const LANDING_PAGE = { host: "app.divecohorts.com", url: "https://www.divecohorts.com/" };

export function startingPoint(hostname, signedIn) {
  if (signedIn) return { hash: "#/feed" };
  if (hostname === LANDING_PAGE.host) return { url: LANDING_PAGE.url };
  return { hash: "#/dive-sites" };
}

// True until restoreSession() has settled; see the guard above.
let booting = true;

async function boot() {
  initVerificationNotices();
  initSearchControl();
  initBell();
  initChatBadge();
  await restoreSession();
  booting = false;
  if (!location.hash) {
    const start = startingPoint(location.hostname, isAuthenticated());
    if (start.url) {
      location.replace(start.url);
    } else {
      location.hash = start.hash;
    }
  } else {
    renderRoute();
  }
}

boot();
